// One 4:3 deck slide: the `Platform Map` H1 + org subtitle, then the matrix
// (left) and the pop-scientist description aside (right) starting at the same
// height, over a TA brand footer. Chrome reuses the live tokens (.snap-h1 /
// .snap-subtitle / .snap-snapshot-notes) so the page reads as a native export.

import type { TimelineSnapshot } from '@lib/api-types/views';

import { DeckMatrix } from '../components/DeckMatrix.tsx';
import type { DeckLayout, DeckPageAxes } from '../deck-layout.ts';

/** TA wordmark served from apps/app/public (white — reads on the dark footer). */
const LOGO_SRC = '/technical-accounting-logo.png';

export interface DeckPageData {
  /** ISO date (used for ordering). */
  date: string;
  /** Display label, preserving the highlight's original granularity. */
  dateLabel?: string;
  title?: string | null;
  /** Pop-scientist topline shown as the description. */
  summary: string;
  snapshot: TimelineSnapshot;
}

interface DeckPageProps {
  /** Repository name(s) shown as the H2 (e.g. "Homebase1"). */
  repos: string;
  data: DeckPageData;
  layout: DeckLayout;
  axes: DeckPageAxes;
  pageNumber: number;
  totalPages: number;
  /** First page = founding era; suppress the (noisy) "everything is new" notes. */
  isFirst: boolean;
}

export function DeckPage({ repos, data, layout, axes, pageNumber, totalPages, isFirst }: DeckPageProps) {
  const notes = isFirst ? [] : buildNotes(layout, axes);
  return (
    <div className='deck-slide'>
      <div className='deck-slide__header'>
        <h1 className='snap-h1'>Platform Map</h1>
        <p className='snap-subtitle'>Timeline for {repos}</p>
      </div>
      <div className='deck-slide__cols'>
        <div className='deck-slide__main'>
          <DeckMatrix snapshot={data.snapshot} layout={layout} axes={axes} />
        </div>
        <aside className='snap-snapshot-notes'>
          <div className='snap-snapshot-notes__date'>{data.dateLabel ?? formatDate(data.date)}</div>
          <p className='snap-snapshot-notes__desc'>{data.summary}</p>
          {notes.length > 0 ? (
            <ul className='snap-snapshot-notes__list'>
              {notes.map((n, i) => (
                <li key={i} className={`snap-snapshot-notes__item snap-snapshot-notes__item--${n.tone}`}>
                  {n.text}
                </li>
              ))}
            </ul>
          ) : null}
        </aside>
      </div>
      <div className='deck-slide__footer'>
        <img src={LOGO_SRC} alt='Technical Accounting' />
        <span className='deck-slide__page'>
          PLATFORM TIMELINE · {pageNumber} / {totalPages}
        </span>
      </div>
    </div>
  );
}

type Note = { tone: 'comp' | 'skill' | 'depr'; text: string };

/** A few factual "what changed" bullets derived from the per-page axis diff. */
function buildNotes(layout: DeckLayout, axes: DeckPageAxes): Note[] {
  const out: Note[] = [];
  const names = (ids: Iterable<string>, display: Map<string, string>) =>
    [...ids].map((id) => display.get(id) ?? id);

  const newComps = names(axes.newCompIds, layout.compDisplay);
  const newBfs = names(axes.newBfIds, layout.bfDisplay);
  const retired = [
    ...names(
      layout.compOrder.filter((id) => axes.compState.get(id) === 'deprecated'),
      layout.compDisplay,
    ),
    ...names(
      layout.bfOrder.filter((id) => axes.bfState.get(id) === 'deprecated'),
      layout.bfDisplay,
    ),
  ];

  if (newComps.length) out.push({ tone: 'comp', text: `New: ${clampList(newComps)}` });
  if (newBfs.length) out.push({ tone: 'skill', text: `Functions online: ${clampList(newBfs)}` });
  if (retired.length) out.push({ tone: 'depr', text: `Retired: ${clampList(retired)}` });
  return out.slice(0, 3);
}

function clampList(names: string[], max = 3): string {
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
